<template>
  <!-- Show Advance Payment in a Popup Modal -->
  <div v-if="showAdvancePaymentModal" ref="modalContent" class="modal-overlay">
    <div class="modal-content" @click.stop style="max-width: 600px;">
      
      <!-- Fixed Header -->
      <div class="modal-header-fixed">
        <div class="popup-header">
          <h4 class="mb-0">New Advance Payment</h4>
          <button @click="closeAdvancePaymentModal" class="btn btn-light close-btn">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
      </div>

      <!-- Scrollable Body -->
      <div class="modal-body-scrollable">
        <div v-if="loadingAdvancePayPopup" class="spinner-container">
          <div class="spinner"></div>
        </div>

        <div v-else>
          <!-- Compact Dealer Information Box -->
          <div class="bg-light rounded p-2 mb-3 border">
            <div class="row">
              <div class="col-6">
                <div class="text-muted" style="font-size: 0.75rem;">Business Name</div>
                <div class="fw-bold" style="font-size: 0.9rem; color: #000;">{{ dealer.business_display_name }}</div>
              </div>
              <div class="col-6">
                <div class="text-muted" style="font-size: 0.75rem;">Address</div>
                <div class="fw-bold" style="font-size: 0.9rem; color: #000;">{{ dealer.address }}</div>
              </div>
            </div>
          </div>

          <!-- Payment Form -->
          <div class="invoice-card-body px-1">
            
            <div class="row gx-3 gy-1">
              <!-- Payment Method -->
              <div class="col-md-6 form-group">
                <label class="form-label mb-1 fw-bold">Payment Method <span class="text-danger">*</span></label>
                <select v-model="selectedPayMethod" class="form-select">
                  <option disabled value="">-- Select Payment Method --</option>
                  <option v-for="method in methodList" :key="method.label" :value="method.label">
                    {{ method.label }}
                  </option>
                </select>
              </div>

              <!-- Conditional Fields based on selectedPayMethod -->
              <!-- Cheque Details -->
              <template v-if="selectedPayMethod === 'Cheque'">
                <div class="col-md-6 form-group">
                  <label class="form-label mb-1 fw-bold">Cheque Issue Bank <span class="text-danger">*</span></label>
                  <select v-model="selectedBank_C" class="form-select">
                    <option value="">-- Select Bank --</option>
                    <option v-for="bank in filteredAllBanks" :key="bank.id" :value="bank">
                      {{ bank.bank_name }}
                    </option>
                  </select>
                </div>
                <div class="col-md-6 form-group">
                  <label class="form-label mb-1 fw-bold">Cheque Issue Branch <span class="text-danger">*</span></label>
                  <select v-model="selectedBranch" class="form-select">
                    <option value="">-- Select Branch --</option>
                    <option v-for="branch in filteredBranches" :key="branch.id" :value="branch">
                      {{ branch.branch_name }}
                    </option>
                  </select>
                </div>
                <div class="col-md-6 form-group">
                  <label class="form-label mb-1 fw-bold">Cheque No <span class="text-danger">*</span></label>
                  <input type="text" v-model="chequeNo" class="form-control" placeholder="Enter Cheque No" />
                </div>
                <div class="col-md-6 form-group">
                  <label class="form-label mb-1 fw-bold">Cheque Issue Date <span class="text-danger">*</span></label>
                  <input type="date" v-model="chequeDate" class="form-control" />
                </div>
              </template>

              <!-- Bank Transfer Details -->
              <template v-else-if="selectedPayMethod === 'Bank Transfer'">
                <div class="col-md-6 form-group">
                  <label class="form-label mb-1 fw-bold">Bank Account <span class="text-danger">*</span></label>
                  <select v-model="selectedBank_BT" class="form-select">
                    <option value="">-- Select Bank Account --</option>
                    <option v-for="bank in bankList" :key="bank.id" :value="bank">
                      {{ formatBankOption(bank) }}
                    </option>
                  </select>
                </div>
                <div class="col-md-6 form-group">
                  <label class="form-label mb-1 fw-bold">Reference Code <span class="text-danger">*</span></label>
                  <input type="text" v-model="refCode" class="form-control" placeholder="Enter Reference Code" />
                </div>
              </template>

              <!-- Credit/Debit Card Details -->
              <template v-else-if="selectedPayMethod === 'Credit/Debit Card'">
                <div class="col-md-6 form-group">
                  <label class="form-label mb-1 fw-bold">Card Type <span class="text-danger">*</span></label>
                  <select v-model="cardType" class="form-select">
                    <option disabled value="">-- Select Card Type --</option>
                    <option value="Visa">Visa</option>
                    <option value="MasterCard">MasterCard</option>
                    <option value="American Express">American Express</option>
                    <option value="Discover">Discover</option>
                    <option value="UnionPay">UnionPay</option>
                    <option value="JCB">JCB</option>
                    <option value="Diners Club">Diners Club</option>
                  </select>
                </div>
                <div class="col-md-6 form-group">
                  <label class="form-label mb-1 fw-bold">Last 4 Digits <span class="text-danger">*</span></label>
                  <input
                    type="text"
                    v-model="cardLastFour"
                    class="form-control"
                    placeholder="e.g. 1234"
                    maxlength="4"
                    @input="cardLastFour = cardLastFour.replace(/\D/g, '')"
                  />
                </div>
              </template>

              <!-- Mobile Payment Details -->
              <template v-else-if="selectedPayMethod === 'Mobile Payment'">
                <div class="col-md-6 form-group">
                  <label class="form-label mb-1 fw-bold">Mobile Payment Type <span class="text-danger">*</span></label>
                  <select v-model="mobilePaymentType" class="form-select">
                    <option disabled value="">-- Select Mobile Payment Type --</option>
                    <option value="QR">QR</option>
                    <option value="Wallet">Wallet</option>
                    <option value="Bank Mobile Apps">Bank Mobile Apps</option>
                  </select>
                </div>
                <div class="col-md-6 form-group">
                  <label class="form-label mb-1 fw-bold">Provider <span class="text-danger">*</span></label>
                  <input type="text" v-model="mobileProvider" class="form-control" placeholder="Enter Provider" />
                </div>
                <div class="col-md-6 form-group">
                  <label class="form-label mb-1 fw-bold">Reference Code <span class="text-danger">*</span></label>
                  <input type="text" v-model="refCode" class="form-control" placeholder="Enter Reference Code" />
                </div>
              </template>

              <!-- Online Payment Details -->
              <template v-else-if="selectedPayMethod === 'Online Payment'">
                <div class="col-md-6 form-group">
                  <label class="form-label mb-1 fw-bold">Payment Gateway <span class="text-danger">*</span></label>
                  <input type="text" v-model="onlineGateway" class="form-control" placeholder="Enter Payment Gateway" />
                </div>
                <div class="col-md-6 form-group">
                  <label class="form-label mb-1 fw-bold">Reference Code <span class="text-danger">*</span></label>
                  <input type="text" v-model="refCode" class="form-control" placeholder="Enter Reference Code" />
                </div>
              </template>

              <!-- Payment Amount (always at the end of the grid) -->
              <div class="col-md-6 form-group">
                <label class="form-label mb-1 fw-bold">Amount (Rs) <span class="text-danger">*</span>
                  <span v-if="total_excess_amount>0" class="text-danger ms-1" style="font-size: 0.8rem;">
                    (Rs. {{ formatNumber(total_excess_amount) }} Excess will auto-deduct)
                  </span>
                </label>
                <input type="number" v-model="amount" class="form-control" placeholder="0.00" />
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="d-flex justify-content-end mt-4 gap-2">
              <button @click="clearForm" class="btn btn-light border">
                Cancel
              </button>
              <button
                @click="saveAdvancePayment"
                :disabled="saveButtonDisabled"
                class="btn btn-success"
              >
                <span v-if="saveButtonDisabled" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                <span v-else>Save</span>
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useToast } from 'vue-toastification';
import {useRouter} from "vue-router";
import {
  getBankList,
  getBanksByLoginUser, getBranchesByBankId,
  saveNewAdvancePayment
} from "@/services/dataService";

export default {
  props: {
    showAdvancePaymentModal:Boolean,
    dealer: Object,
    excessAmount: Number,
    totalDue: Number
  },

  data() {
    return {
      token: localStorage.getItem('token_set') || '',
      profileId: localStorage.getItem('profile_id'),
      loadingAdvancePayPopup: false,
      allbankList:[],
      filteredAllBanks:[],
      bankList:[],
      selectedBank_C:"",
      selectedBank_BT:"",
      branchesList:[],
      filteredBranches:[],
      chequeNo:"",
      chequeDate:"",  // ✅ Added chequeDate to data()
      refCode:"",
      selectedBranch:"",
      selectedPayMethod:"",
      cardType: "",
      cardLastFour: "",
      mobilePaymentType: "",
      mobileProvider: "",
      onlineGateway: "",
      methodList: [
        { id: 1, label: "Cash" },
        { id: 2, label: "Cheque" },
        { id: 3, label: "Bank Transfer" },
        { id: 4, label: "Credit/Debit Card" },
        { id: 5, label: "Mobile Payment" },
        { id: 6, label: "Online Payment" },
      ],
      saveButtonDisabled:false,
      amount:""
    };
  },

  watch: {
    loadingAdvancePayPopup(val) {
      this.toggleBodyScroll();
    },
    selectedPayMethod(){
      this.resetPopup();
    },
    selectedBank_C(newVal) {
      if (newVal) {
        this.successmessage = "";
        this.selectedBranch = "";
        this.loadBankBranchList();
      }
    }
  },

  created(){
    this.loadBankList();
    this.loadAllBankList();
  },

  methods: {
    formatBankOption(bank) {
      const acc_no = bank.account_no;
      const last4 = acc_no.toString().slice(-4);
      const acc = '••••••••' + last4;
      return `${bank.bank_name} - A/C No: ${acc}`;
    },

    formatNumber(value) {
      if (!value) return '0.00';
      return parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    toggleBodyScroll() {
      document.body.style.overflow = this.loadingAdvancePayPopup ? 'hidden' : '';
    },

    async loadBankList() {
      try {
        const response = await getBanksByLoginUser(this.profileId,this.token);
        this.bankList = response.data.data || [];
      } catch (error) {
        console.error('Error fetching Branches:', error);
      }
    },

    async loadAllBankList() {
      try {
        const response = await getBankList(this.token);
        this.allbankList = response.data.data || [];
        this.filteredAllBanks=[...this.allbankList];
      } catch (error) {
        console.error('Error fetching all banks:', error);
      }
    },

    async loadBankBranchList() {
      try {
        const payload = { bank_id: this.selectedBank_C.id };
        const response = await getBranchesByBankId(payload,this.token);
        this.branchesList = response.data.data|| [];
        this.filteredBranches=[...this.branchesList];
      } catch (error) {
        console.error('Error fetching Branches:', error);
      }
    },

    filterAllBanks(query) {
      this.filteredAllBanks = this.allbankList.filter(bank =>
          bank.bank_name.toLowerCase().includes(query.toLowerCase())
      );
    },

    filterBranches(query) {
      this.filteredBranches = this.branchesList.filter(branch =>
          branch.branch_name.toLowerCase().includes(query.toLowerCase())
      );
    },

    closeAdvancePaymentModal() {
      this.amount= 0;
      this.selectedPayMethod = "";
      this.resetPopup();
      this.$emit('close');
    },

    resetPopup(){
      this.chequeNo="";
      this.chequeDate="";  // ✅ reset cheque date when popup resets
      this.selectedBank_C="" ;
      this.selectedBank_BT="" ;
      this.selectedBranch="";
      this.refCode="";
      this.cardType = "";
      this.cardLastFour = "";
      this.mobilePaymentType = "";
      this.mobileProvider = "";
      this.onlineGateway = "";
    },

    clearForm() {
      this.amount = "";
      this.selectedPayMethod = "";
      this.resetPopup();
    },

    async saveAdvancePayment(){
      if(this.saveButtonDisabled)return;
      this.saveButtonDisabled = true;

      if(!this.selectedPayMethod){
        useToast().error("Please select a payment method.");
        this.saveButtonDisabled = false;
        return;
      }

      if(!this.amount){
        useToast().error("Please enter an amount.");
        this.saveButtonDisabled = false;
        return;
      }

      const payload = {
        dealer_id:this.dealer.profile_id,
        amount:this.amount,
        pay_method:this.selectedPayMethod,
        loginprofileId: this.profileId
      }

      if(this.selectedPayMethod === 'Cheque'){
        if(!this.chequeDate){
          useToast().error("Please select a cheque date.");
          this.saveButtonDisabled = false;
          return;
        }
        payload.cheque_no = this.chequeNo||'';
        payload.cheque_date = this.chequeDate;  // ✅ send cheque date to API
        payload.cheque_issue_bank_id = this.selectedBank_C.id || null;
        payload.cheque_issue_branch_id = this.selectedBranch?.id || null;
      }

      if(this.selectedPayMethod === 'Bank Transfer'){
        payload.trans_ref_code = this.refCode || '';
        payload.distributor_bank_id = this.selectedBank_BT.id || null;
      }

      if (this.selectedPayMethod === 'Credit/Debit Card') {
        if (!this.cardType || !this.cardLastFour) {
          useToast().error("Please fill in card details.");
          this.saveButtonDisabled = false;
          return;
        }
        payload.card_type = this.cardType;
        payload.card_last4_digits = this.cardLastFour;
      }

      if (this.selectedPayMethod === 'Mobile Payment') {
        if (!this.mobilePaymentType || !this.mobileProvider || !this.refCode) {
          useToast().error("Please fill in mobile payment details.");
          this.saveButtonDisabled = false;
          return;
        }
        payload.mobile_payment_type = this.mobilePaymentType;
        payload.mobile_provider = this.mobileProvider;
        payload.trans_ref_code = this.refCode;
      }

      if (this.selectedPayMethod === 'Online Payment') {
        if (!this.onlineGateway || !this.refCode) {
          useToast().error("Please fill in online payment details.");
          this.saveButtonDisabled = false;
          return;
        }
        payload.online_gateway = this.onlineGateway;
        payload.trans_ref_code = this.refCode;
      }

      console.log('Final advance payment payload:', payload);

      try{
        await saveNewAdvancePayment(this.token,payload);
        useToast().success("Advance payment saved successfully.");
        this.closeAdvancePaymentModal();
      }catch(error){
        console.error("Error response:", error.response);

        if (error.response && error.response.data && error.response.data.errors) {
          Object.values(error.response.data.errors).forEach((messages) => {
            messages.forEach((message) => {
              useToast().error(message);
            });
          });
        }
      }finally {
        this.saveButtonDisabled = false;
      }
    }
  }
};
</script>


<style scoped>

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
  max-width: 100%;;
  width: 90%;
  max-height: 100%;
  overflow-y: auto;
}

.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top:20px;
  margin-bottom:20px;
}

.disabled-icon {
  color: #ccc !important;
  cursor: not-allowed !important;
  opacity: 0.6;
}

.excess-note{
  font-size: 13px;
  color: #888;
  margin-left: 5px;
  font-weight:normal;
}

.status-pending {
  background-color: #FFC107; /* Yellow color */
  color: #FFFFFF; /* White text */
  padding: 5px 10px;
  border-radius: 5px;
}

.status-completed {
  background-color: #069E27;
  color: #fff;
  border-radius: 4px;
}

.status-cancelled {
  background-color: red;
  color: #fff;
  border-radius: 4px;
}
.status-redeemed{
  background-color: #1B6CB4;
  color: #fff;
  border-radius: 4px;
}

.status-label {
  padding: 5px 10px;
  border-radius: 3px;
  display: inline-block;
}

.styled-bank-dropdown .multiselect__option {
  display: flex;
  flex-direction: column;
  padding: 8px;
  font-size: 14px;
}

.styled-bank-dropdown .multiselect__option span {
  color: #666;
  font-size: 12px;
}

html, body {
  height: 100%;
  margin: 0;
  padding: 0;
  background-color: #f0f0f0;
}

.card .table-responsive {
  overflow-x: visible !important;
  -webkit-overflow-scrolling: auto !important;
}

.card {
  padding: 20px;
  margin-bottom: 40px !important;
}

.cards{
  display:flex;
  gap:100px;
}

.req {
  color: red;
  margin-left: 5px;
}

.searchbar{
  flex: 1 !important;
  max-width: 45% !important;
  margin-bottom:1rem;
}

/* Popup modal */
.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  border-radius: 5px !important;
}

.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.close-btn {
  border-radius: 50%;
  width: 33px;
  height: 33px;
  padding: 0;
  cursor: pointer;
  font-size: 18px;
}

.popup-table {
  max-height: 300px;
  overflow-y: auto;
}

.invoice-popup-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
  margin-top: 30px;
}

.invoice-popup-table th, .invoice-popup-table td {
  border: 1px solid #ddd;
  padding: 8px;
  text-align: left;
}

.invoice-popup-table th {
  background-color: #f2f2f2;
  font-weight: bold;
}

.dropdown-container {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
  width: 100%;
  justify-content: center;
  margin-bottom: 20px;
}

.dropdown-container-popup{
  display: grid;
  grid-template-columns: repeat(2, 1fr); /*grid-template-columns: 60% 40%;*/
  gap: 15px;
  width: 100%;
  justify-content: center;
  padding: 30px;
}

.dropdown-item {
  flex: 1;
  max-width: 90%;
}

.details-container .dropdown-item {
  margin-left:10px;
  max-width: 30%;
}

.details-container-popup .dropdown-item {
  margin-left:10px;
  max-width: 50%;
}

.payment{
  margin-top: 30px;
}

label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  font-size: 14px;
}

select {
  width: 100%; /**/
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px;
  transition: border-color 0.3s, box-shadow 0.3s;
}

input[type="date"] {
  width: 100%;
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px;
  transition: border-color 0.3s, box-shadow 0.3s;
}

input[type="number"] {
  width: 100%;
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px;
  transition: border-color 0.3s, box-shadow 0.3s;
}

input[type="text"] {
  width: 100%;
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px;
  transition: border-color 0.3s, box-shadow 0.3s;
}

select:focus {
  border-color: #007bff;
  box-shadow: 0 0 5px rgba(38, 143, 255, 0.5);
  outline: none;
}

input:focus {
  border-color: #007bff;
  box-shadow: 0 0 5px rgba(38, 143, 255, 0.5);
  outline: none;
}


.fullscreen-loader {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5); /* semi-transparent overlay */
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.spinner {
  width: 60px;
  height: 60px;
  border: 5px solid rgba(255, 255, 255, 0.3);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin 1s ease infinite;
}

:deep(.multiselect__spinner::before,
  .multiselect__spinner::after) {
  border-color: #007bff transparent transparent;
}

:deep(.multiselect__tag)
{
  background: #007bff;
}

:deep(.multiselect__option--highlight){
  background: #007bff;
}

:deep(.multiselect__option--highlight::after){
  background: #007bff;
}

:deep(.multiselect__option--selected.multiselect__option--highlight){
  background: #007bff;
}

:deep(.multiselect__option--selected.multiselect__option--highlight::after){
  background: #007bff;
}

:deep(.multiselect__option--group-selected.multiselect__option--highlight ){
  background: #007bff;
}

:deep(.multiselect__option--group-selected.multiselect__option--highlight::after){
  background: #007bff;
}

.card .card-body {
  padding: 0px;
  background-color: transparent;
}

.text-dark{
  font-size:20px;
}

.method-details-section {
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  margin-top: 20px;
  margin-left: 30px;
  margin-right: 30px;
  overflow: hidden;
}

.method-details-header {
  background-color: #e9ecef;
  padding: 10px 20px;
  border-bottom: 1px solid #dee2e6;
}

.method-details-header h6 {
  margin: 0;
  color: #495057;
  font-weight: 600;
}

.pb-2 {
  padding-bottom: 10px;
}

.pt-2 {
  padding-top: 10px;
}

</style>